import { getAccountsQueryConfig } from '@api/lendingAPI/queryConfigs';
import { getFormalOfferInterestOnlyPeriod } from '@helpers/FormalOffers/getFormalOfferInterestOnlyPeriod';
import {
    fetchGetDrawDownRepaymentScheduleCreditFacility,
    fetchGetDrawDownRepaymentScheduleFlexi24,
    GetAccountsResponse,
    GetDrawDownRepaymentScheduleCreditFacilityResponse,
    GetDrawDownRepaymentScheduleFlexi24Response
} from '@iwoca/lapi-client/edge';
import { useQuery } from '@tanstack/react-query';
import { get } from 'lodash';

import { useStateKey } from '../../../../iwQueryClient/useStateKey';
import { getLoanDurationInMonths } from '../../../myOffers/MyOffers.helpers';

type TGetRepaymentScheduleSummaryData = {
    durationInMonths: number;
    isInterestOnlyLoan: boolean;
    finalMonthsRepaymentAmount: number;
    equalMonthlyAmount: number;
    totalInterest: number;
    totalBalance: number;
    fees: number;
    blendedRateDetails: TDrawdownRepaymentSchedule['summary']['blended_rate_details'];
    interestOnlyPeriod: number | undefined;
};
type TLatestFormalOffer =
    GetAccountsResponse['data']['accounts'][0]['latest_formal_offer'];
type TDrawdownRepaymentSchedule =
    | GetDrawDownRepaymentScheduleCreditFacilityResponse['data']
    | GetDrawDownRepaymentScheduleFlexi24Response['data'];

export const useGetRepaymentScheduleSummaryData = (
    accountId: string,
    topupDrawdownAmount: number
): TGetRepaymentScheduleSummaryData => {
    const stateKey = useStateKey();

    const { data: account } = useQuery({
        ...getAccountsQueryConfig(stateKey),
        select: (data) =>
            data.data.accounts.find(
                (account) => account.account_id === accountId
            ),
        suspense: true
    });

    const { data: drawdownSchedule } = useQuery({
        queryKey: [
            'fetchGetDrawDownRepaymentScheduleCreditFacility',
            stateKey,
            account?.latest_formal_offer.offer_id!,
            topupDrawdownAmount
        ],
        queryFn: () =>
            fetchGetDrawDownRepaymentScheduleCreditFacility({
                stateKey: stateKey,
                offerId: account?.latest_formal_offer.offer_id!,
                query: { amount: topupDrawdownAmount }
            }),
        enabled:
            Boolean(account) &&
            account?.latest_formal_offer?.product_type === 'credit_facility',
        suspense: true
    });

    const { data: drawdownScheduleFlexi24 } = useQuery({
        queryKey: [
            'fetchGetDrawDownRepaymentScheduleFlexi24',
            stateKey,
            account?.latest_formal_offer.offer_id!,
            topupDrawdownAmount
        ],
        queryFn: () =>
            fetchGetDrawDownRepaymentScheduleFlexi24({
                stateKey: stateKey,
                offerId: account?.latest_formal_offer.offer_id!,
                query: { amount: topupDrawdownAmount }
            }),
        enabled:
            Boolean(account) &&
            account?.latest_formal_offer.product_type === 'flexi24',
        suspense: true
    });

    if (!account || (!drawdownSchedule && !drawdownScheduleFlexi24)) {
        throw new Error();
    }

    const latestFormalOffer = account.latest_formal_offer;
    const repaymentSchedule = drawdownSchedule
        ? drawdownSchedule.data.schedule!
        : drawdownScheduleFlexi24?.data.schedule!;
    const repaymentSummary = drawdownSchedule
        ? drawdownSchedule.data.summary!
        : drawdownScheduleFlexi24?.data.summary!;

    return {
        durationInMonths: getLoanDurationInMonths(
            latestFormalOffer?.max_duration!
        ),
        isInterestOnlyLoan: isInterestOnlyLoan(latestFormalOffer),
        interestOnlyPeriod: getFormalOfferInterestOnlyPeriod(latestFormalOffer),
        finalMonthsRepaymentAmount:
            finalMonthsRepaymentAmount(repaymentSchedule),
        equalMonthlyAmount: equalMonthlyAmount(repaymentSummary),
        totalInterest: getDrawdownTotalInterest(repaymentSchedule),
        totalBalance: getDrawdownTotalBalance(repaymentSummary),
        fees: getDrawdownFees(repaymentSummary),
        blendedRateDetails: repaymentSummary?.blended_rate_details
    };
};

const isInterestOnlyLoan = (latestFormalOffer: TLatestFormalOffer) => {
    return Boolean(latestFormalOffer.interest_only_repayments);
};

const finalMonthsRepaymentAmount = (
    repaymentSchedule: TDrawdownRepaymentSchedule['schedule']
) => {
    return repaymentSchedule?.[repaymentSchedule.length - 1]?.total || 0;
};

const equalMonthlyAmount = (
    repaymentSummary: TDrawdownRepaymentSchedule['summary']
) => {
    return get(
        repaymentSummary,
        'simplified_schedule.equal_repayments.amount',
        0
    );
};

const getDrawdownTotalInterest = (
    repaymentSchedule: TDrawdownRepaymentSchedule['schedule']
) => {
    return repaymentSchedule.reduce(
        (totalInterest, repayment) => totalInterest + repayment.interest,
        0
    );
};

const getDrawdownTotalBalance = (
    repaymentSummary: TDrawdownRepaymentSchedule['summary']
) => {
    return get(repaymentSummary, 'total_payable', 0);
};

const getDrawdownFees = (
    repaymentSummary: TDrawdownRepaymentSchedule['summary']
) => {
    return get(repaymentSummary, 'total_fees', 0);
};
